import {useState} from 'react'
import {SectionDivider} from '@/components/shared/SectionDivider'
import {Edit, Settings} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {MutedText} from '@/components/shared/MutedText'
import {FormGrid} from '@/components/shared/FormGrid'
import {Stack} from '@/components/shared/Stack'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {DatabaseType} from '@/types/outbound-connector'
import type {OutboundConnector, DatabaseOutboundConfigCreateRequest, DatabaseOutboundConfigUpdateRequest} from '@/types/outbound-connector'

 
const DATABASE_TYPE_OPTIONS = [
    {value: 'POSTGRESQL', labelKey: 'databaseTypePostgresql' as const},
    {value: 'MYSQL', labelKey: 'databaseTypeMysql' as const},
    {value: 'ORACLE', labelKey: 'databaseTypeOracle' as const},
    {value: 'SQLSERVER', labelKey: 'databaseTypeSqlserver' as const},
    {value: 'OTHER', labelKey: 'databaseTypeOther' as const},
] as const
const DEFAULT_TEST_SQL = 'SELECT 1'
const DEFAULT_DRIVER_CLASS = 'org.postgresql.Driver'
 

interface DatabaseFormState {
    databaseType: DatabaseType
    jdbcUrl: string
    driverClass: string
    username: string
    passwordRef: string
    grantSqlTemplate: string
    revokeSqlTemplate: string
    testSql: string
    connectTimeoutMs: number
    queryTimeoutMs: number
    version: number
}

const DEFAULT_DATABASE_FORM: DatabaseFormState = {
    databaseType: 'POSTGRESQL',
    jdbcUrl: '',
    driverClass: DEFAULT_DRIVER_CLASS,
    username: '',
    passwordRef: '',
    grantSqlTemplate: '',
    revokeSqlTemplate: '',
    testSql: DEFAULT_TEST_SQL,
    connectTimeoutMs: 5000,
    queryTimeoutMs: 30000,
    version: 0,
}

interface DatabaseConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

export function DatabaseConfigCard({connector, canUpdate}: DatabaseConfigCardProps) {
    const services = useServices()
    const i18n = useI18n()
    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<DatabaseFormState>(DEFAULT_DATABASE_FORM)

    const updateForm = (updates: Partial<DatabaseFormState>) => setForm((prev) => ({...prev, ...updates}))

    const createMutation = useEntityMutation({
        mutationFn: (data: DatabaseOutboundConfigCreateRequest) =>
            services.outboundConnectors.createDatabaseConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: createDialog.close,
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: DatabaseOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateDatabaseConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: editDialog.close,
    })

    const openCreate = () => {
        setForm(DEFAULT_DATABASE_FORM)
        createDialog.open()
    }

    const openEdit = () => {
        const config = connector.databaseConfig
        if (!config) return
        setForm({
            databaseType: config.databaseType,
            jdbcUrl: config.jdbcUrl,
            driverClass: config.driverClass,
            username: config.username,
            passwordRef: '',
            grantSqlTemplate: config.grantSqlTemplate,
            revokeSqlTemplate: config.revokeSqlTemplate,
            testSql: config.testSql ?? '',
            connectTimeoutMs: config.connectTimeoutMs,
            queryTimeoutMs: config.queryTimeoutMs,
            version: config.version,
        })
        editDialog.open()
    }

    const handleCreate = () => {
        const request: DatabaseOutboundConfigCreateRequest = {
            databaseType: form.databaseType,
            jdbcUrl: form.jdbcUrl,
            driverClass: form.driverClass,
            username: form.username,
            passwordRef: form.passwordRef,
            grantSqlTemplate: form.grantSqlTemplate,
            revokeSqlTemplate: form.revokeSqlTemplate,
            testSql: form.testSql || undefined,
            connectTimeoutMs: form.connectTimeoutMs || undefined,
            queryTimeoutMs: form.queryTimeoutMs || undefined,
        }
        createMutation.mutate(request)
    }

    const handleUpdate = () => {
        const request: DatabaseOutboundConfigUpdateRequest = {
            databaseType: form.databaseType,
            jdbcUrl: form.jdbcUrl,
            driverClass: form.driverClass,
            username: form.username,
            passwordRef: form.passwordRef,
            grantSqlTemplate: form.grantSqlTemplate,
            revokeSqlTemplate: form.revokeSqlTemplate,
            testSql: form.testSql || null,
            connectTimeoutMs: form.connectTimeoutMs,
            queryTimeoutMs: form.queryTimeoutMs,
            version: form.version,
        }
        updateMutation.mutate(request)
    }

    const dbConfig = connector.databaseConfig

    const formFields = (mode: 'create' | 'edit') => {
        const handleSubmit = () => {
            if (mode === 'create') {
                handleCreate()
            } else {
                handleUpdate()
            }
        }
        return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className="grid gap-6 py-4">
                <Stack>
                    <Strong>{i18n.connectors.databaseConfiguration}</Strong>
                    <FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor="db-databaseType">{i18n.connectors.databaseType}</Label>
                                <Select value={form.databaseType}
                                    onValueChange={(v) => updateForm({databaseType: v as DatabaseType})}>
                                    <SelectTrigger id="db-databaseType" data-testid="select-database-type"><SelectValue/></SelectTrigger>
                                    <SelectContent>
                                        {DATABASE_TYPE_OPTIONS.map(({value, labelKey}) => (
                                            <SelectItem key={value} value={value}>{i18n.connectors[labelKey]}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="db-driverClass">{i18n.connectors.driverClass}</Label>
                                <Input id="db-driverClass" value={form.driverClass}
                                    onChange={(e) => updateForm({driverClass: e.target.value})}
                                    placeholder={i18n.connectors.driverClassPlaceholder}/>
                            </FormGrid>
                        </DetailGrid>
                        <FormGrid>
                            <Label htmlFor="db-jdbcUrl">{i18n.connectors.jdbcUrl}</Label>
                            <Input id="db-jdbcUrl" value={form.jdbcUrl}
                                onChange={(e) => updateForm({jdbcUrl: e.target.value})}
                                placeholder={i18n.connectors.jdbcUrlPlaceholder}/>
                        </FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor="db-username">{i18n.connectors.dbUsername}</Label>
                                <Input id="db-username" value={form.username}
                                    onChange={(e) => updateForm({username: e.target.value})}
                                    placeholder={i18n.connectors.dbUsernamePlaceholder}/>
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="db-passwordRef">{i18n.connectors.passwordRef}</Label>
                                <Input id="db-passwordRef" value={form.passwordRef}
                                    onChange={(e) => updateForm({passwordRef: e.target.value})}
                                    placeholder={i18n.connectors.passwordRefPlaceholder}/>
                                <MutedText size="sm">{i18n.connectors.passwordRefHint}</MutedText>
                            </FormGrid>
                        </DetailGrid>
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.sqlTemplates}</Strong>
                    <FormGrid>
                        <FormGrid>
                            <Label htmlFor="db-grantSqlTemplate">{i18n.connectors.grantSqlTemplate}</Label>
                            <Input id="db-grantSqlTemplate" value={form.grantSqlTemplate}
                                onChange={(e) => updateForm({grantSqlTemplate: e.target.value})}
                                placeholder={i18n.connectors.grantSqlTemplatePlaceholder}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="db-revokeSqlTemplate">{i18n.connectors.revokeSqlTemplate}</Label>
                            <Input id="db-revokeSqlTemplate" value={form.revokeSqlTemplate}
                                onChange={(e) => updateForm({revokeSqlTemplate: e.target.value})}
                                placeholder={i18n.connectors.revokeSqlTemplatePlaceholder}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="db-testSql">{i18n.connectors.testSql}</Label>
                            <Input id="db-testSql" value={form.testSql}
                                onChange={(e) => updateForm({testSql: e.target.value})}
                                placeholder={i18n.connectors.testSqlPlaceholder}/>
                            <MutedText size="sm">{i18n.connectors.testSqlHint}</MutedText>
                        </FormGrid>
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.connectionSettings}</Strong>
                    <DetailGrid columns={2} responsive={false}>
                        <FormGrid>
                            <Label htmlFor="db-connectTimeoutMs">{i18n.connectors.connectTimeoutMs}</Label>
                            <Input id="db-connectTimeoutMs" type="number" value={form.connectTimeoutMs}
                                onChange={(e) => updateForm({connectTimeoutMs: parseInt(e.target.value)})}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="db-queryTimeoutMs">{i18n.connectors.queryTimeoutMs}</Label>
                            <Input id="db-queryTimeoutMs" type="number" value={form.queryTimeoutMs}
                                onChange={(e) => updateForm({queryTimeoutMs: parseInt(e.target.value)})}/>
                        </FormGrid>
                    </DetailGrid>
                </Stack>
            </div>
            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-database' : 'edit-database'}
                onCancel={() => mode === 'create' ? createDialog.close() : editDialog.close()}
                saving={mode === 'create' ? createMutation.isPending : updateMutation.isPending}
                disabled={!form.jdbcUrl || !form.driverClass || !form.username || !form.passwordRef || !form.grantSqlTemplate || !form.revokeSqlTemplate}
                saveLabel={mode === 'create' ? i18n.connectors.saveConfiguration : i18n.common.saveChanges}
            />
        </form>
        )
    }

    return (
        <>
            <Card data-testid="database-config-card">
                <RowCardHeader>
                    <div>
                        <CardTitle className="items-center gap-2" data-testid="card-title-database-config">
                            <Settings className="h-5 w-5"/>
                            {i18n.connectors.databaseConfiguration}
                        </CardTitle>
                        <CardDescription>{i18n.connectors.databaseDescription}</CardDescription>
                    </div>
                    {canUpdate && (
                        dbConfig ? (
                            <Button data-testid="btn-edit-database-config" size="sm" onClick={openEdit}>
                                <Icon icon={Edit} className="mr-2"/>
                                {i18n.common.edit}
                            </Button>
                        ) : (
                            <Button data-testid="btn-create-database-config" size="sm" onClick={openCreate}>
                                <Icon icon={Settings} className="mr-2"/>
                                {i18n.connectors.configure}
                            </Button>
                        )
                    )}
                </RowCardHeader>
                <CardContent>
                    {dbConfig ? (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.databaseType}</dt>
                                <dd><CodeText>{dbConfig.databaseType}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.dbUsername}</dt>
                                <dd><CodeText>{dbConfig.username}</CodeText></dd>
                            </div>
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.jdbcUrl}</dt>
                                <dd><CodeText>{dbConfig.jdbcUrl}</CodeText></dd>
                            </div>
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.driverClass}</dt>
                                <dd><CodeText>{dbConfig.driverClass}</CodeText></dd>
                            </div>
                            <SectionDivider colSpan={2}>
                                <Strong className="text-sm mb-2">{i18n.connectors.sqlTemplates}</Strong>
                            </SectionDivider>
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.grantSqlTemplate}</dt>
                                <dd><CodeText>{dbConfig.grantSqlTemplate}</CodeText></dd>
                            </div>
                            <div className="sm:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.revokeSqlTemplate}</dt>
                                <dd><CodeText>{dbConfig.revokeSqlTemplate}</CodeText></dd>
                            </div>
                            {dbConfig.testSql && (
                                <div className="sm:col-span-2">
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.testSql}</dt>
                                    <dd className="mt-1 font-mono text-sm">{dbConfig.testSql}</dd>
                                </div>
                            )}
                            <SectionDivider colSpan={2}>
                                <Strong className="text-sm mb-2">{i18n.connectors.connectionSettings}</Strong>
                            </SectionDivider>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.connectTimeoutMs}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: dbConfig.connectTimeoutMs})}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.queryTimeoutMs}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: dbConfig.queryTimeoutMs})}</dd>
                            </div>
                        </dl>
                    ) : (
                        <MutedText block centered className="py-4">{i18n.connectors.databaseNotConfigured}</MutedText>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.configureDatabaseConnection}</DialogTitle>
                        <DialogDescription>{i18n.connectors.configureDatabaseDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('create')}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.editDatabaseConfiguration}</DialogTitle>
                        <DialogDescription>{i18n.connectors.editDatabaseDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('edit')}
                </DialogContent>
            </Dialog>
        </>
    )
}
